import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, Building2, Printer, Bell } from "lucide-react";

export default function Settings() {
  const { tenant, reload } = useAuth();
  const { setTheme } = useTheme();
  const { t, setLang } = useI18n();
  const qc = useQueryClient();
  const [form, setForm] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  useEffect(() => { if (data) setForm(data); }, [data]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/settings", {
      company_name: form.company_name, logo: form.logo, language: form.language,
      theme: form.theme, currency: form.currency, thermal_printer: form.thermal_printer, notifications: form.notifications,
    })).data,
    onSuccess: () => { toast.success("Settings saved"); if (form.theme) setTheme(form.theme); if (form.language) setLang(form.language); qc.invalidateQueries({ queryKey: ["settings"] }); reload?.(); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  if (isLoading || !form)
    return <div className="space-y-6"><PageHeader title={t("set.title")} breadcrumb={[t("crumb.center"), t("set.title")]} /><Skeleton className="h-96" /></div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPrinter = (k, v) => setForm((f) => ({ ...f, thermal_printer: { ...f.thermal_printer, [k]: v } }));
  const setNotif = (k, v) => setForm((f) => ({ ...f, notifications: { ...f.notifications, [k]: v } }));

  return (
    <div className="space-y-6" data-testid="settings-page">
      <PageHeader
        title={t("set.title")}
        subtitle={tenant?.center_name}
        breadcrumb={[t("crumb.center"), t("set.title")]}
        actions={<Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="save-settings-btn">{save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} {t("common.saveChanges")}</Button>}
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 font-heading text-lg"><Building2 className="h-5 w-5 text-primary" /> {t("set.companyAppearance")}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5"><Label>{t("set.companyName")}</Label><Input value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} data-testid="settings-company-input" /></div>
            <div className="space-y-1.5"><Label>{t("set.logo")}</Label><Input value={form.logo || ""} onChange={(e) => set("logo", e.target.value)} data-testid="settings-logo-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t("common.language")}</Label>
                <Select value={form.language || "en"} onValueChange={(v) => set("language", v)}>
                  <SelectTrigger data-testid="settings-language-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="en">English</SelectItem><SelectItem value="mr">मराठी (Marathi)</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("set.theme")}</Label>
                <Select value={form.theme || "light"} onValueChange={(v) => set("theme", v)}>
                  <SelectTrigger data-testid="settings-theme-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="light">{t("set.light")}</SelectItem><SelectItem value="dark">{t("set.dark")}</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t("set.currency")}</Label>
              <Select value={form.currency || "INR"} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger data-testid="settings-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="INR">INR ₹</SelectItem><SelectItem value="USD">USD $</SelectItem></SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 font-heading text-lg"><Printer className="h-5 w-5 text-primary" /> {t("set.printer")}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>{t("set.paperWidth")}</Label>
                <Select value={String(form.thermal_printer?.width_mm || 58)} onValueChange={(v) => setPrinter("width_mm", Number(v))}>
                  <SelectTrigger data-testid="settings-printer-width"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="58">58 mm</SelectItem><SelectItem value="80">80 mm</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>{t("set.header")}</Label><Input value={form.thermal_printer?.header || ""} onChange={(e) => setPrinter("header", e.target.value)} data-testid="settings-printer-header" /></div>
              <div className="space-y-1.5"><Label>{t("set.footer")}</Label><Input value={form.thermal_printer?.footer || ""} onChange={(e) => setPrinter("footer", e.target.value)} data-testid="settings-printer-footer" /></div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 font-heading text-lg"><Bell className="h-5 w-5 text-primary" /> {t("set.notifications")}</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {[["email", t("set.emailActive")], ["sms", t("set.smsSoon")], ["whatsapp", t("set.whatsappSoon")], ["push", t("set.pushSoon")]].map(([k, label]) => (
                <div key={k} className="flex items-center justify-between">
                  <Label className="font-normal">{label}</Label>
                  <Switch checked={!!form.notifications?.[k]} onCheckedChange={(v) => setNotif(k, v)} data-testid={`settings-notif-${k}`} />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
